import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { MapPin, Clock, ArrowRight, Camera } from "lucide-react";
import { webcams } from "../data/mock";
import WebcamPlayer from "../components/WebcamPlayer";
import WebcamSnapshot from "../components/WebcamSnapshot";
import WeatherWidget from "../components/WeatherWidget";

const WebcamPage = () => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [now, setNow] = useState(new Date());

  const handleRefresh = () => setRefreshKey((prev) => prev + 1);

  // Live Adelaide clock — ticks every 30s
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(id);
  }, []);

  const adelaideTime = now.toLocaleString("en-AU", {
    timeZone: "Australia/Adelaide",
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  return (
    <div className="min-h-screen">
      {/* 1. Compact cinema hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        {/* Subtle radial orange glow at top */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage:
              "radial-gradient(ellipse at top, rgba(251,146,60,0.12), transparent 55%)",
          }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <div className="space-y-5 max-w-3xl">
              <div className="animate-fade-in">
                <Badge className="bg-orange-500/20 text-orange-300 border border-orange-400/30 hover:bg-orange-500/30 backdrop-blur-sm px-4 py-2 text-sm">
                  <span className="inline-block w-2 h-2 bg-red-500 rounded-full mr-2 animate-pulse"></span>
                  LIVE FROM COOBER PEDY
                </Badge>
              </div>

              <h1 className="text-4xl lg:text-6xl font-black text-white leading-tight tracking-tight animate-slide-up-1">
                Live{" "}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-yellow-300 to-orange-500">
                  Webcams
                </span>
              </h1>

              <p className="text-lg lg:text-xl text-gray-300 leading-relaxed animate-slide-up-2">
                Real-time views from the opal mining town of Coober Pedy in the
                South Australian outback — streamed 24/7 from the rooftop of
                Umoona Opal Mine &amp; Museum.
              </p>
            </div>

            {/* Live Adelaide clock pill */}
            <div className="hidden lg:flex items-center gap-4 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl px-5 py-4 animate-fade-in-delayed">
              <div className="w-12 h-12 rounded-full bg-orange-500/20 border border-orange-400/30 flex items-center justify-center">
                <Clock className="w-6 h-6 text-orange-300" />
              </div>
              <div>
                <div className="text-xs uppercase tracking-wider text-gray-400 font-semibold">
                  Local Time
                </div>
                <div className="text-xl font-bold text-white tabular-nums">
                  {adelaideTime}
                </div>
                <div className="text-xs text-gray-400">Australia/Adelaide</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Featured cameras — stacked vertically */}
      <section className="bg-slate-950 pt-2 pb-10 lg:pb-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-8 lg:gap-12">
            {webcams.cameras.map((camera) => (
              <article
                key={camera.id}
                className="group rounded-2xl overflow-hidden border border-white/10 bg-slate-900 shadow-2xl shadow-black/50 hover:border-orange-400/40 transition-colors duration-300"
              >
                <header className="px-4 py-3 flex items-center justify-between border-b border-white/10">
                  <div className="flex items-center gap-2 min-w-0">
                    <MapPin className="w-4 h-4 text-orange-400 flex-shrink-0" />
                    <h2 className="text-white font-semibold text-base truncate">
                      {camera.name}
                    </h2>
                  </div>
                  <span className="text-xs text-gray-400 uppercase tracking-wider hidden sm:inline">
                    Coober Pedy, SA
                  </span>
                </header>

                {camera.mode === "snapshot" ? (
                  <WebcamSnapshot
                    snapshotUrl={camera.snapshotUrl}
                    aspectRatio={camera.aspectRatio}
                    refreshKey={refreshKey}
                    onRefresh={handleRefresh}
                  />
                ) : (
                  <WebcamPlayer
                    streamUrl={camera.streamUrl}
                    posterUrl={camera.posterUrl}
                    aspectRatio={camera.aspectRatio}
                    refreshKey={refreshKey}
                    onRefresh={handleRefresh}
                  />
                )}

                <footer className="px-4 py-3 border-t border-white/10 space-y-2">
                  <p className="text-sm text-gray-300 leading-relaxed">
                    {camera.description}
                  </p>
                  {camera.mode === "snapshot" && (
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-blue-300">
                      <Camera className="w-3.5 h-3.5" />
                      <span>Image refreshes every 60 seconds</span>
                    </div>
                  )}
                </footer>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* 3. Weather widget — live outside temp + underground constant */}
      <WeatherWidget />

      {/* 4. Slim about strip */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-6">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">
            About these views
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed">
            The cameras sit on the rooftop of Umoona Opal Mine &amp; Museum.
            One looks down <span className="font-semibold">Hutchison Street</span>,
            the main drag of Coober Pedy. The other looks out across the
            surrounding <span className="font-semibold">opal fields</span> —
            the lunar-like mining landscape that's made this desert town
            famous since the 1920s.
          </p>
          <div className="pt-2">
            <Link
              to="/coober-pedy"
              className="inline-flex items-center gap-2 text-orange-600 hover:text-orange-700 font-semibold text-lg transition-colors"
            >
              Learn more about Coober Pedy
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* 5. On-brand CTA */}
      <section className="py-20 bg-gradient-to-r from-amber-600 to-orange-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="space-y-8">
            <div className="space-y-4">
              <h2 className="text-3xl lg:text-4xl font-bold">
                Ready to experience it in person?
              </h2>
              <p className="text-xl text-orange-50 max-w-2xl mx-auto">
                The webcams are just the beginning. Come underground for the
                full mine tour, free museum access, and authentic Australian
                opals.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="bg-white text-orange-700 hover:bg-orange-50 font-semibold px-8 py-4 text-lg shadow-lg"
              >
                <Link to="/book-tour">Book Underground Tour</Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="border-2 border-white text-white hover:bg-white hover:text-orange-700 font-semibold px-8 py-4 text-lg bg-transparent"
              >
                <Link to="/">Visit the Museum</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WebcamPage;
